import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/tailwind";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring  select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "font-medium bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "font-medium bg-destructive text-destructive-foreground shadow-sm hover:text-destructive-background hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm text-neutral-500 hover:bg-accent hover:text-accent-foreground",
        // think vscode buttons (see cmd+f options)
        minimal: "bg-background text-gray-500 hover:bg-gray-100",
        secondary:
          "font-medium bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "font-medium hover:bg-accent hover:text-accent-foreground",
        link: "font-medium text-primary underline-offset-4 hover:underline transition-all duration-75",
        kino: "bg-indigo-600 font-regular text-white hover:text-indigo-100 shadow-md hover:shadow-lg hover:bg-indigo-600/90",
      },
      size: {
        default: "h-9 px-4 py-2",
        defaultCircle: "h-9 px-4 py-2 rounded-full",
        xsm: "h-7 rounded-md text-xs px-2",
        xsmSquare: "h-7 w-7 rounded-md  text-xs",
        xsmCircle: "h-5 w-5 text-xs rounded-full",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        footer: "h-6 px-4 py-2 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
